import React from "react";
import { useAuth } from "../auth/useAuth";
import type { CheckInViewSettings } from "../checkin/checkinView";
import { loadCheckInView, saveCheckInView } from "../checkin/checkinViewStorage";
import type { UnitsSettings } from "../units/units";
import { saveUnits } from "../units/unitsStorage";
import type { PinnedLink } from "../pins/pinnedLinks";
import { normalizeStoredNavigationTargets } from "../navigation/storedNavigationTargets";
import type { PersistedSorter } from "../utils/proTableSortPersistence";
import type { ReportPeriodPreference } from "../../pages/reports/components/reportPeriodPreferences";
import { combineUiPreferences, loadDeviceUiPreferences, saveDeviceUiPreferences, splitUiPreferences } from "./deviceUiPreferences";

const SAVE_DEBOUNCE_MS = 400;

export type UiPreferences = {
  menu?: {
    collapsed?: boolean;
    openKeys?: string[];
  };
  startPage?: {
    /**
     * Hash-router path, e.g. "/dashboard" or "/checkin".
     * null/undefined => use default.
     */
    path?: string | null;
  };
  units?: UnitsSettings;
  checkInView?: CheckInViewSettings;
  calendar?: {
    studioId?: string | null;
    /** Kalender Tag/Woche und Dienstplan füllen die Bildschirmhöhe statt der Standard-Zeilenhöhe. */
    fillScreen?: boolean | null;
    /** Einstellungen → Verfügbarkeiten füllen die Bildschirmhöhe statt der Standard-Zeilenhöhe. */
    availabilityFillScreen?: boolean | null;
    /** Reihenfolge der Ressourcen-Spalten (IDs). Leer = Standardreihenfolge. */
    resourceOrderIds?: string[] | null;
    /** Ressourcen und Mitarbeiter ohne Verfügbarkeit in Tag-/Wochenansicht ausblenden. */
    hideResourcesWithoutAvailability?: boolean | null;
    /** Zuletzt gebuchte Terminart je Ressourcen-/Mitarbeiter-Lane (id → appointmentTypeId), für Vorauswahl. */
    lastTypeByResource?: Record<string, string> | null;
    /** Gespeicherter Ansichtsmodus: Tag, Woche, Monat, Liste. */
    viewMode?: "today" | "week" | "month" | "list" | null;
  };
  staffSchedule?: {
    /**
     * Mitarbeiter ausblenden, die an diesem Standort üblicherweise nicht arbeiten:
     * kein Einsatz in den vier Wochen vor der angezeigten Woche und keine Schicht darin.
     */
    hideAccountsWithoutRecentActivity?: boolean | null;
  };
  checkInPage?: {
    studioId?: string | null;
    viewActive?: "grid" | "list";
    viewHistory?: "grid" | "list";
    historyWindowMinutes?: number;
    cardSize?: "S" | "M" | "L" | "XL";
  };
  /** Zuletzt verwendete Ausgabe in den Dokument-Editoren — persönliche Arbeitspräferenz. */
  documentEditors?: {
    appearanceSurface?: "letter" | "email" | "whatsapp";
    /** Persönliche Arbeitsansicht der Vorlagenpflege; kein Vorlagenbestandteil. */
    previewValues?: boolean;
    /** Unabhängige Versandbearbeitung: standardmäßig echte Vorgangswerte statt Variablen. */
    sendPreviewValues?: boolean;
    /** @deprecated Altwert vor der gemeinsamen Dokumentvorschau. */
    appearancePreviewValues?: boolean;
    systemDocumentSurface?: "letter" | "email";
    /** @deprecated Altwert vor der gemeinsamen Dokumentvorschau. */
    systemDocumentPreviewValues?: boolean;
  };
  /** Globaler Sichtkontext (Header): leer/null/[] = alle zugreifbaren Studios. */
  studioContext?: {
    studioIds?: string[] | null;
  };
  dashboardPage?: {
    /** Optionaler Studio-Filter für Dashboard-APIs. Leer/undefined => alle zugreifbaren Studios. */
    studioIds?: string[] | null;
  };
  reports?: Record<string, {
    period?: ReportPeriodPreference;
  }>;
  proTableSort?: Record<string, PersistedSorter>;
  /** Schluessel: Tabellen-Kennung, Wert: gespeicherte Spaltenkonfiguration. */
  tableColumns?: Record<string, unknown[]>; // array
  emailSpooler?: {
    status?: string | null;
  };
  /** Admin-Bereich: eigener Status-Filter für E-Mail-Spooler */
  adminEmailSpooler?: {
    status?: string | null;
  };
  /** Duplikatfinder: Kriterien, Gewichte, Schwellwerte (server-side pro Benutzer). */
  duplicateFinder?: {
    firstName?: { enabled: boolean; weight: number };
    lastName?: { enabled: boolean; weight: number };
    birthDate?: { enabled: boolean; weight: number };
    email?: { enabled: boolean; weight: number };
    phone?: { enabled: boolean; weight: number };
    street?: { enabled: boolean; weight: number };
    zip?: { enabled: boolean; weight: number };
    city?: { enabled: boolean; weight: number };
    iban?: { enabled: boolean; weight: number };
    scoreThreshold?: number;
    minCheckableCriteria?: number;
    gmailNormalization?: boolean;
    usePhonetic?: boolean;
  };
  centerComImportRun?: {
    staffUsageRightFiltersByRunId?: Record<string, string[]>;
  };
  /** Persönliche Pins in der Titelleiste (max. MAX_PINNED_LINKS Einträge). */
  pinnedLinks?: {
    items?: PinnedLink[];
  };
  /**
   * Manuelle Zahlungserfassung: zuletzt gewählte Zahlungsart als persönliche Arbeitspräferenz.
   * Gilt gemeinsam für alle Masken, in denen ein Mitarbeiter eine tatsächlich geflossene Zahlung
   * erfasst — nie für die vereinbarte Zahlart an Vertrag, Beitrag oder offenem Posten.
   */
  paymentEntry?: {
    lastMethod?: number | null;
  };
  /** Anordnung der Blöcke in der Übersicht der Personenakte (Zahnrad, wie das Dashboard). */
  personOverviewLayout?: {
    widgets?: Array<{ key: string; visible: boolean; limit?: number }>;
    widgetOrder?: string[];
  };
  /**
   * Einklappzustand der Formularabschnitte. Schlüssel ist der stabile `sectionKey` von
   * `FormSectionHeading`. `true` = eingeklappt, `false` = ausgeklappt; fehlender Schlüssel = Vorgabe der Stelle.
   */
  collapsedFormSections?: Record<string, boolean>;
};

type UiPreferencesContextValue = {
  prefs: UiPreferences;
  isReady: boolean;
  setPrefs: (next: UiPreferences) => void;
  patchPrefs: (patch: Partial<UiPreferences>) => void;
  reload: () => Promise<void>;
};

const UiPreferencesContext = React.createContext<UiPreferencesContextValue | null>(null);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function mergePrefs(base: UiPreferences, patch: Partial<UiPreferences>): UiPreferences {
  const next: UiPreferences = { ...base, ...patch };
  if (patch.menu) next.menu = { ...(base.menu ?? {}), ...(patch.menu ?? {}) };
  if (patch.startPage) next.startPage = { ...(base.startPage ?? {}), ...(patch.startPage ?? {}) };
  if (patch.units) next.units = { ...(base.units ?? {}), ...(patch.units ?? {}) };
  if (patch.calendar) next.calendar = { ...(base.calendar ?? {}), ...(patch.calendar ?? {}) };
  if (patch.checkInPage) next.checkInPage = { ...(base.checkInPage ?? {}), ...(patch.checkInPage ?? {}) };
  if (patch.documentEditors) next.documentEditors = { ...(base.documentEditors ?? {}), ...(patch.documentEditors ?? {}) };
  if (patch.studioContext !== undefined) {
    if (patch.studioContext == null) {
      delete next.studioContext;
    } else {
      next.studioContext = { ...(base.studioContext ?? {}), ...(patch.studioContext ?? {}) };
      const ids = next.studioContext.studioIds;
      if (ids == null || (Array.isArray(ids) && ids.length === 0)) {
        delete next.studioContext.studioIds;
        if (next.studioContext && Object.keys(next.studioContext).length === 0) delete next.studioContext;
      }
    }
  }
  if (patch.dashboardPage) next.dashboardPage = { ...(base.dashboardPage ?? {}), ...(patch.dashboardPage ?? {}) };
  if (patch.reports) {
    next.reports = { ...(base.reports ?? {}) };
    for (const [reportKey, reportPatch] of Object.entries(patch.reports)) {
      if (reportPatch == null) {
        delete next.reports![reportKey];
        continue;
      }
      next.reports![reportKey] = {
        ...(base.reports?.[reportKey] ?? {}),
        ...(reportPatch ?? {})
      };
      if (reportPatch.period) {
        next.reports![reportKey].period = {
          ...(base.reports?.[reportKey]?.period ?? {}),
          ...(reportPatch.period ?? {})
        };
      }
      if (Object.keys(next.reports![reportKey]).length === 0) {
        delete next.reports![reportKey];
      }
    }
    if (Object.keys(next.reports).length === 0) delete next.reports;
  }
  // Nicht nur { ...base, ...patch }: fehlende Keys in patch würden Einträge aus base nicht entfernen.
  // `null`/`undefined` als Patch-Wert entfernt den Tabellen-Key (z. B. Sortierung zurücksetzen).
  if (patch.proTableSort) {
    next.proTableSort = { ...(base.proTableSort ?? {}) };
    const p = patch.proTableSort as Record<string, PersistedSorter | null | undefined>;
    for (const [k, v] of Object.entries(p)) {
      if (v === null || v === undefined) delete next.proTableSort![k];
      else next.proTableSort![k] = v;
    }
  }
  if (patch.tableColumns) next.tableColumns = { ...(base.tableColumns ?? {}), ...(patch.tableColumns ?? {}) };
  if (patch.paymentEntry) next.paymentEntry = { ...(base.paymentEntry ?? {}), ...(patch.paymentEntry ?? {}) };
  if (patch.checkInView) next.checkInView = patch.checkInView;
  if (patch.emailSpooler) next.emailSpooler = { ...(base.emailSpooler ?? {}), ...(patch.emailSpooler ?? {}) };
  if (patch.adminEmailSpooler) next.adminEmailSpooler = { ...(base.adminEmailSpooler ?? {}), ...(patch.adminEmailSpooler ?? {}) };
  if (patch.duplicateFinder) next.duplicateFinder = { ...(base.duplicateFinder ?? {}), ...(patch.duplicateFinder ?? {}) };
  if (patch.centerComImportRun) {
    next.centerComImportRun = {
      ...(base.centerComImportRun ?? {}),
      ...(patch.centerComImportRun ?? {}),
      staffUsageRightFiltersByRunId: {
        ...(base.centerComImportRun?.staffUsageRightFiltersByRunId ?? {}),
        ...(patch.centerComImportRun.staffUsageRightFiltersByRunId ?? {})
      }
    };
  }
  if (patch.collapsedFormSections) {
    next.collapsedFormSections = { ...(base.collapsedFormSections ?? {}) };
    // Auch „ausgeklappt“ bleibt gespeichert: Abschnitte mit eingeklappter Vorgabe merken sich so das Öffnen.
    for (const [sectionKey, collapsed] of Object.entries(patch.collapsedFormSections)) next.collapsedFormSections[sectionKey] = collapsed === true;
  }
  return next;
}


export function UiPreferencesProvider(props: { children: React.ReactNode }) {
  const auth = useAuth();
  const accountId = auth.info?.accountId ? String(auth.info.accountId) : null;

  const [prefs, setPrefsState] = React.useState<UiPreferences>({});
  const [isReady, setIsReady] = React.useState(false);

  const saveTimer = React.useRef<number | null>(null);
  const reloadGeneration = React.useRef(0);
  const userSnapshot = React.useRef<string>("{}");

  const flushToServer = React.useCallback(
    (next: UiPreferences) => {
      if (!accountId || !auth.token) return;
      if (typeof window === "undefined") return;
      const user = splitUiPreferences(next).user;
      const serialized = JSON.stringify(user);
      // Reine Darstellungsänderungen lösen keine serverseitige Speicherung aus.
      if (serialized === userSnapshot.current) return;
      userSnapshot.current = serialized;

      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => {
        auth.api.putUiPreferences(user).catch((e: unknown) => {
          if (userSnapshot.current === serialized) userSnapshot.current = "";
          console.error("Konnte UI-Preferences nicht speichern:", e);
        });
      }, SAVE_DEBOUNCE_MS);
    },
    [accountId, auth.api, auth.token]
  );

  const setPrefs = React.useCallback(
    (next: UiPreferences) => {
      setPrefsState(next);
      flushToServer(next);

      if (accountId) {
        saveDeviceUiPreferences(accountId, next);
        if (next.units) saveUnits(next.units, accountId);
        if (next.checkInView) saveCheckInView(next.checkInView, accountId);
      }
    },
    [accountId, flushToServer]
  );

  const patchPrefs = React.useCallback(
    (patch: Partial<UiPreferences>) => {
      setPrefsState((prev) => {
        const next = mergePrefs(prev, patch);
        // mirror behavior of setPrefs
        flushToServer(next);
        if (accountId) {
          saveDeviceUiPreferences(accountId, next);
          if (next.units) saveUnits(next.units, accountId);
          if (next.checkInView) saveCheckInView(next.checkInView, accountId);
        }
        return next;
      });
    },
    [accountId, flushToServer]
  );

  const reload = React.useCallback(async () => {
    const generation = ++reloadGeneration.current;
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    if (!accountId || !auth.token) {
      setPrefsState({});
      setIsReady(true);
      return;
    }

    setIsReady(false);
    const loadLocal = () => {
      const local = loadDeviceUiPreferences(accountId);
      // Bestehendes gerätelokales Design erhalten, niemals aus Server-Prefs übernehmen.
      return {
        ...local,
        checkInView: { ...loadCheckInView(accountId), ...local.checkInView },
      };
    };
    try {
      const raw = await auth.api.getUiPreferences<unknown>();
      const server: UiPreferences = isPlainObject(raw) ? (raw as UiPreferences) : {};
      if (generation !== reloadGeneration.current) return;
      const original = combineUiPreferences(server, loadLocal());
      const combined = normalizeStoredNavigationTargets(original);
      userSnapshot.current = JSON.stringify(splitUiPreferences(combined).user);

      if (combined.units) saveUnits(combined.units, accountId);
      if (combined.checkInView) saveCheckInView(combined.checkInView, accountId);

      setPrefsState(combined);
      setIsReady(true);
      // Gerätelokale Altwerte der Klappzustände einmalig ans Konto übergeben.
      if ((server.collapsedFormSections === undefined && combined.collapsedFormSections) || JSON.stringify(original) !== JSON.stringify(combined)) {
        userSnapshot.current = "";
        flushToServer(combined);
      }
    } catch (e) {
      if (generation !== reloadGeneration.current) return;
      console.error("Konnte UI-Preferences nicht laden:", e);
      setPrefsState(normalizeStoredNavigationTargets(combineUiPreferences({}, loadLocal())));
      setIsReady(true);
    }
  }, [accountId, auth.api, auth.token, flushToServer]);

  const cancelReload = React.useCallback(() => {
    ++reloadGeneration.current;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
  }, []);

  React.useEffect(() => {
    // account changed or login/logout
    reload().catch(() => {
      // handled in reload
    });
    return cancelReload;
  }, [reload, cancelReload]);

  const value = React.useMemo<UiPreferencesContextValue>(
    () => ({ prefs, isReady, setPrefs, patchPrefs, reload }),
    [prefs, isReady, setPrefs, patchPrefs, reload]
  );

  return <UiPreferencesContext.Provider value={value}>{props.children}</UiPreferencesContext.Provider>;
}

export function useUiPreferences() {
  const ctx = React.useContext(UiPreferencesContext);
  if (!ctx) throw new Error("useUiPreferences muss innerhalb von UiPreferencesProvider verwendet werden.");
  return ctx;
}
